// The canonical KPI compliance aggregation, on the calendar-month axis.
//
// KPI cadences are heterogeneous — daily through annual all occur on one
// contract — so a headline that averages by raw period key cannot place a
// daily result and a quarterly one on the same scale. Everything here
// normalises onto calendar months instead:
//
//   - A result belongs to the month its reporting period CLOSES in.
//   - A KPI's score for a month is the MEAN of its own results that month, so
//     a daily KPI logging thirty results carries the same weight as a monthly
//     KPI logging one. Weights are green 1, amber 0.5, red 0.
//   - A month's compliance is the equal-weighted mean across the KPIs that
//     reported in it, and is NULL when none did — never zero, which would
//     read as total failure rather than as no measurement.
//   - A range average is the mean of the non-null months, taken over the
//     UNROUNDED month values so rounding is applied once instead of
//     compounding.
//
// The same definition backs the KPI tab headline and the KPI compliance
// report, and is bound to the backend composer by
// tests/shared/kpiComplianceFixtures/canonicalAggregation.json (ADR-015). Any
// change here must update that fixture and keep both stacks green.
//
// This is a leaf module on purpose: the KPI tab's summary helpers and the
// report surface both depend on it, and code shared that widely must not sit
// inside a module that tests routinely mock.

import type { RagRating } from "../../types";
import { normaliseFrequency } from "./period";
import { periodCloseFromKey } from "./periodClose";

/** Weights are locked: amber is partial credit for a KPI inside its tolerance
 *  band, not a silent pass and not a flat failure. */
const RATING_WEIGHT: Record<RagRating, number> = {
  green: 1,
  amber: 0.5,
  red: 0,
};

/** A KPI definition, reduced to what the aggregation needs. Cadence comes
 *  from the measurement frequency — the same basis the schedule derives
 *  period keys from, so a period lands in the same month on both surfaces. */
export interface ComplianceKpi {
  kpiId: string;
  measurementFrequency: string;
}

export interface ComplianceResult {
  kpiId: string;
  period: string;
  rating: RagRating;
  /** Used only to pick a winner when one period is logged twice. */
  recordedAt?: string | null;
}

export interface MonthCompliancePoint {
  month: string;
  /** Rounded to one decimal for display. Null when no KPI reported. */
  weightedPct: number | null;
}

export interface MonthlyCompliance {
  months: MonthCompliancePoint[];
  /** Mean of the non-null months, rounded once. Null when none reported. */
  rangeWeightedPct: number | null;
}

// ─── Month arithmetic ─────────────────────────────────────────────────────

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Months since year zero, so two month keys can be compared and stepped
 *  without constructing dates. A string that is not `YYYY-MM` yields NaN,
 *  which propagates rather than being caught here: every caller builds its own
 *  keys, so a bad one is a programming error and not a runtime condition to
 *  render around. */
function monthIndex(month: string): number {
  return Number(month.slice(0, 4)) * 12 + (Number(month.slice(5, 7)) - 1);
}

function monthFromIndex(index: number): string {
  return `${Math.floor(index / 12)}-${pad2((index % 12) + 1)}`;
}

/** The `YYYY-MM` a `YYYY-MM-DD` date falls in. */
export function monthOfDate(date: Date): string {
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}`;
}

export function addMonths(month: string, delta: number): string {
  return monthFromIndex(monthIndex(month) + delta);
}

/** Every month from `start` to `end`, both ends included. Empty when the
 *  range runs backwards. */
export function monthSpan(start: string, end: string): string[] {
  const from = monthIndex(start);
  const to = monthIndex(end);
  const months: string[] = [];
  for (let i = from; i <= to; i += 1) months.push(monthFromIndex(i));
  return months;
}

// ─── Placement ────────────────────────────────────────────────────────────

/** The closing month of a result, or null when its period key does not parse
 *  for its KPI's cadence. Stored period strings are free text and a KPI's
 *  frequency can be changed after results exist, so a mismatched key is a row
 *  to skip — never a reason to fail the whole headline. */
function closingMonth(period: string, frequency: string): string | null {
  try {
    return periodCloseFromKey(period, normaliseFrequency(frequency)).slice(
      0,
      7,
    );
  } catch {
    return null;
  }
}

export interface PlacedResult {
  kpiId: string;
  month: string;
  rating: RagRating;
}

/** One result per (KPI, period), most recently recorded winning, each placed
 *  on its closing month. Results for unknown KPIs and unparseable keys drop
 *  out here. */
export function placeResultsOnMonths(
  kpis: ComplianceKpi[],
  results: ComplianceResult[],
): PlacedResult[] {
  const frequencyByKpi = new Map(
    kpis.map((kpi) => [kpi.kpiId, kpi.measurementFrequency]),
  );

  const latest = new Map<string, ComplianceResult>();
  for (const result of results) {
    if (!frequencyByKpi.has(result.kpiId)) continue;
    const key = `${result.kpiId}::${result.period}`;
    const existing = latest.get(key);
    if (!existing || (result.recordedAt ?? "") >= (existing.recordedAt ?? "")) {
      latest.set(key, result);
    }
  }

  const placed: PlacedResult[] = [];
  for (const result of latest.values()) {
    const month = closingMonth(
      result.period,
      frequencyByKpi.get(result.kpiId)!,
    );
    if (month === null) continue;
    placed.push({ kpiId: result.kpiId, month, rating: result.rating });
  }
  return placed;
}

/** The earliest month any result closes in, for a contract-to-date window.
 *  Null when nothing places. */
export function earliestClosingMonth(
  kpis: ComplianceKpi[],
  results: ComplianceResult[],
): string | null {
  let earliest: string | null = null;
  for (const { month } of placeResultsOnMonths(kpis, results)) {
    if (earliest === null || month < earliest) earliest = month;
  }
  return earliest;
}

/** Results grouped by KPI and month, keyed for lookup during aggregation. */
function groupResultsByKpiMonth(
  kpis: ComplianceKpi[],
  results: ComplianceResult[],
): Map<string, RagRating[]> {
  const grouped = new Map<string, RagRating[]>();
  for (const { kpiId, month, rating } of placeResultsOnMonths(kpis, results)) {
    const key = `${kpiId}::${month}`;
    const bucket = grouped.get(key);
    if (bucket) bucket.push(rating);
    else grouped.set(key, [rating]);
  }
  return grouped;
}

// ─── Aggregation ──────────────────────────────────────────────────────────

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function mean(values: number[]): number {
  return values.reduce((acc, value) => acc + value, 0) / values.length;
}

export function computeMonthlyCompliance(
  kpis: ComplianceKpi[],
  results: ComplianceResult[],
  months: string[],
): MonthlyCompliance {
  const grouped = groupResultsByKpiMonth(kpis, results);

  const raw = months.map((month) => {
    const scores: number[] = [];
    for (const kpi of kpis) {
      const ratings = grouped.get(`${kpi.kpiId}::${month}`);
      if (!ratings || ratings.length === 0) continue;
      scores.push(mean(ratings.map((rating) => RATING_WEIGHT[rating])));
    }
    return scores.length === 0 ? null : mean(scores) * 100;
  });

  const reported = raw.filter((value): value is number => value !== null);

  return {
    months: months.map((month, index) => ({
      month,
      weightedPct: raw[index] === null ? null : round1(raw[index]),
    })),
    rangeWeightedPct: reported.length === 0 ? null : round1(mean(reported)),
  };
}
